import React, { Component } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "react-select";
import defaultMenuRenderer from "react-select/lib/utils/defaultMenuRenderer";

const DEFAULT_PAGE_SIZE = 20;

class SearchablePaginatedSelect extends Component {
    state = {
        isLoadingMore: false,
        page: 1,
        searchValue: ""
    };
    loadRequestId = 0;
    loadingMoreInFlight = false;
    menuScrollElement = null;
    isUnmounted = false;

    getPageSize = () => {
        const pageSize = parseInt(this.props.pageSize, 10);
        return pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
    };

    getCurrentPage = () => {
        return this.props.page > 0 ? this.props.page : this.state.page;
    };

    getFilteredOptions = () => {
        const { options = [], labelKey = "label" } = this.props;
        const searchValue = this.state.searchValue.trim().toLowerCase();

        if (!searchValue) {
            return options;
        }

        return options.filter(option => String(option[labelKey] || "").toLowerCase().includes(searchValue));
    };

    getPageCount = () => {
        return Math.max(1, Math.ceil(this.getFilteredOptions().length / this.getPageSize()));
    };

    getPageOptions = () => {
        const pageSize = this.getPageSize();
        const currentPage = Math.min(this.getCurrentPage(), this.getPageCount());

        return this.getFilteredOptions().slice(0, currentPage * pageSize);
    };

    hasMore = () => {
        const { hasMore } = this.props;

        if (typeof hasMore === "function") {
            return hasMore({
                page: this.getCurrentPage(),
                pageSize: this.getPageSize(),
                searchValue: this.state.searchValue
            });
        }

        if (typeof hasMore === "boolean") {
            return hasMore;
        }

        return this.getCurrentPage() < this.getPageCount();
    };

    getSelectOptions = () => {
        const { options = [], value, valueKey = "value" } = this.props;
        const pageOptions = this.getPageOptions();
        const values = Array.isArray(value) ? value : [value];
        const selectedValues = values
            .filter(selectedValue => selectedValue !== undefined && selectedValue !== null)
            .map(selectedValue => {
                return typeof selectedValue === "object" ? selectedValue[valueKey] : selectedValue;
            });
        const selectedOptions = options.filter(option => {
            return selectedValues.some(selectedValue => String(option[valueKey]) === String(selectedValue));
        });

        return selectedOptions.reduce((selectOptions, option) => {
            const isAlreadyIncluded = selectOptions.some(pageOption => String(pageOption[valueKey]) === String(option[valueKey]));
            return isAlreadyIncluded ? selectOptions : selectOptions.concat(option);
        }, pageOptions);
    };

    filterPageOptions = () => {
        return this.getPageOptions();
    };

    componentDidUpdate = () => {
        const pageCount = this.getPageCount();
        if (!this.props.page && this.state.page > pageCount) {
            this.setState({ page: pageCount });
        }
    };

    componentWillUnmount = () => {
        this.isUnmounted = true;
        this.loadRequestId += 1;
        this.detachMenuScrollListener();
    };

    setPage = page => {
        const nextPage = Math.max(1, Math.min(page, this.getPageCount()));

        if (!this.props.page) {
            this.setState({ page: nextPage });
        }

        if (this.props.onPageChange) {
            this.props.onPageChange(nextPage);
        }
    };

    notifyPageReset = () => {
        if (this.getCurrentPage() !== 1 && this.props.onPageChange) {
            this.props.onPageChange(1);
        }
    };

    resetPendingLoad = () => {
        this.loadRequestId += 1;
        this.loadingMoreInFlight = false;
        if (!this.isUnmounted && this.state.isLoadingMore) {
            this.setState({ isLoadingMore: false });
        }
    };

    handleInputChange = value => {
        let searchValue = value || "";

        if (this.props.onInputChange) {
            const nextSearchValue = this.props.onInputChange(searchValue);
            if (nextSearchValue !== undefined && nextSearchValue !== null && typeof nextSearchValue !== "object") {
                searchValue = String(nextSearchValue);
            }
        }

        this.resetPendingLoad();
        this.notifyPageReset();
        this.setState({
            page: 1,
            searchValue
        });

        if (this.props.onSearchChange) {
            this.props.onSearchChange(searchValue);
        }

        return searchValue;
    };

    handleChange = value => {
        this.resetPendingLoad();
        this.notifyPageReset();
        this.setState({
            page: 1,
            searchValue: ""
        });

        if (this.props.onChange) {
            this.props.onChange(value);
        }
    };

    handleOpen = () => {
        this.notifyPageReset();
        this.setState({ page: 1 });

        if (this.props.onOpen) {
            this.props.onOpen();
        }
    };

    handleMenuScroll = event => {
        const target = event.currentTarget;
        const configuredThreshold = parseInt(this.props.loadMoreThreshold, 10);
        const threshold = configuredThreshold >= 0 ? configuredThreshold : 96;
        const distanceFromEnd = target.scrollHeight - target.clientHeight - target.scrollTop;

        if (target.scrollHeight > target.clientHeight && distanceFromEnd <= threshold) {
            this.loadMore();
        }
    };

    loadMore = () => {
        if (this.props.isLoading || this.loadingMoreInFlight || !this.hasMore()) {
            return;
        }

        if (this.props.onMenuScrollToBottom) {
            this.props.onMenuScrollToBottom();
        }

        this.loadingMoreInFlight = true;
        const nextPage = this.getCurrentPage() + 1;
        const { onLoadMore } = this.props;

        if (!onLoadMore) {
            this.setState({ isLoadingMore: true }, () => {
                this.setPage(nextPage);
                this.finishLoadingMore(this.loadRequestId);
            });
            return;
        }

        let loadResult;
        const requestId = ++this.loadRequestId;
        this.setState({ isLoadingMore: true });

        try {
            loadResult = onLoadMore({
                page: nextPage,
                pageSize: this.getPageSize(),
                searchValue: this.state.searchValue
            });
        } catch (error) {
            this.finishLoadingMore(requestId, error);
            return;
        }

        if (!loadResult || typeof loadResult.then !== "function") {
            this.setPage(nextPage);
            this.finishLoadingMore(requestId);
            return;
        }

        loadResult.then(
            () => {
                if (this.isUnmounted || requestId !== this.loadRequestId) {
                    return;
                }
                this.setPage(nextPage);
                this.finishLoadingMore(requestId);
            },
            error => this.finishLoadingMore(requestId, error)
        );
    };

    finishLoadingMore = (requestId, error) => {
        if (this.isUnmounted || requestId !== this.loadRequestId) {
            return;
        }

        if (error && this.props.onLoadMoreError) {
            this.props.onLoadMoreError(error);
        }

        this.loadingMoreInFlight = false;
        this.setState({ isLoadingMore: false });
    };

    setMenuContentRef = element => {
        const nextScrollElement = element ? element.parentNode : null;

        if (nextScrollElement === this.menuScrollElement) {
            return;
        }

        this.detachMenuScrollListener();
        this.menuScrollElement = nextScrollElement;

        if (this.menuScrollElement) {
            this.menuScrollElement.addEventListener("scroll", this.handleMenuScroll);
        }
    };

    detachMenuScrollListener = () => {
        if (this.menuScrollElement) {
            this.menuScrollElement.removeEventListener("scroll", this.handleMenuScroll);
            this.menuScrollElement = null;
        }
    };

    keepMenuOpen = event => {
        event.stopPropagation();
        event.preventDefault();
    };

    renderLoadingIndicator = () => {
        if (!this.props.isLoading && !this.state.isLoadingMore) {
            return null;
        }

        const { loadingMoreText = "Loading more..." } = this.props;

        return (
            <div
                className="searchable-paginated-select__loading"
                onMouseDown={this.keepMenuOpen}
                role="status"
                style={{
                    alignItems: "center",
                    background: "#ffffff",
                    borderTop: "1px solid rgba(255, 12, 156, 0.25)",
                    display: "flex",
                    justifyContent: "center",
                    minHeight: "42px",
                    padding: "6px 8px"
                }}
            >
                <CircularProgress size={18} style={{ color: "#ff0c9c", marginRight: "8px" }} />
                <span>{loadingMoreText}</span>
            </div>
        );
    };

    renderMenu = menuProps => {
        return (
            <div ref={this.setMenuContentRef}>
                {defaultMenuRenderer(menuProps)}
                {this.renderLoadingIndicator()}
            </div>
        );
    };

    render() {
        const {
            hasMore,
            isLoading,
            loadMoreThreshold,
            loadingMoreText,
            onChange,
            onInputChange,
            onLoadMore,
            onLoadMoreError,
            onMenuScrollToBottom,
            onOpen,
            options,
            page,
            pageSize,
            onPageChange,
            onSearchChange,
            ...selectProps
        } = this.props;

        return (
            <Select
                {...selectProps}
                options={this.getSelectOptions()}
                filterOptions={this.filterPageOptions}
                isLoading={Boolean(isLoading || this.state.isLoadingMore)}
                onChange={this.handleChange}
                onInputChange={this.handleInputChange}
                onOpen={this.handleOpen}
                menuRenderer={this.renderMenu}
            />
        );
    }
}

export default SearchablePaginatedSelect;